<template>
  <div id="ebso-screen">
    <jumbotron :title="$t('ebsoScreen.welcome.title')" :description="$t('ebsoScreen.welcome.description')"
      :buttonText="$t('ebsoScreen.welcome.buyButton')" :buttonType="'light-green'" @buttonClicked="handleBuyClick"
      :titleColor="'#fff'" :textColor="'#fff'" :background="'#050535'"
      :backgroundImage="'/images/sections/ebso/hero-background.jpg'" :mobileHeight="415"
      :logoSrc="'/images/logos/ebso-logo.svg'"
      :mobileBackgroundImage="'/images/sections/ebso/hero-background.jpg'">
      <template v-slot:badge>
        <div class="mica-badge">
          <img src="/images/icons/mica-licensed.svg" class="mica-badge__icon" alt="" />
          <span class="mica-badge__label">{{ $t('ebsoScreen.welcome.micaBadge') }}</span>
        </div>
      </template>
    </jumbotron>

    <links-section :items="externalLinks" />

    <holoverz-white-paper :text="$t('ebsoScreen.whitepaper.text')" :buttonText="$t('ebsoScreen.whitepaper.button')"
      :buttonLink="whitepaperUrl" :background="'#050535'"
      :buttonType="'light-green'" :buttonSide="'left'" :accentColor="'#01dba8'"
      :buttonTextTransform="'uppercase'" :textColor="'#E8E8FF'" :borderTop="'1px solid #6464FF'" />

    <split-section :title="$t('ebsoScreen.web3Innovation.title')" :body="$t('ebsoScreen.web3Innovation.body')"
      :imageAlt="$t('ebsoScreen.web3Innovation.imageAlt')" imageSide="right"
      :imageSrc="'/images/sections/ebso/web3-innovation.jpg'" />

    <split-section :title="$t('ebsoScreen.bsoVsEbso.title')" :body="$t('ebsoScreen.bsoVsEbso.body')"
      :imageAlt="$t('ebsoScreen.bsoVsEbso.imageAlt')" imageSide="right"
      :imageSrc="`/images/sections/ebso/bso-vs-ebso${imageLocaleSuffix}.jpg`" :visualAspectRatio="'1830 / 1146'"
      :contentPaddingY="'50px'">
      <template v-slot:footer>
        <span class="supply-pill">{{ $t('ebsoScreen.bsoVsEbso.supply') }}</span>
      </template>
    </split-section>

    <card-grid-section :title="$t('ebsoScreen.whatYouBuy.title')" :items="whatYouBuyItems" :columns="2"
      :borderTop="'1px solid #6464FF'" />

    <split-section :title="$t('ebsoScreen.growingUtility.title')" :body="$t('ebsoScreen.growingUtility.body')"
      :imageAlt="$t('ebsoScreen.growingUtility.imageAlt')" imageSide="right"
      :imageSrc="`/images/sections/ebso/growing-utility${imageLocaleSuffix}.jpg`" :visualAspectRatio="'1830 / 1225'"
      :contentPaddingY="'50px'" :borderTop="'1px solid #6464FF'" />

    <split-section :title="$t('ebsoScreen.lockBso.title')" :body="$t('ebsoScreen.lockBso.body')"
      :imageAlt="$t('ebsoScreen.lockBso.imageAlt')" imageSide="left" :backgroundColor="'#040428'"
      :imageSrc="'/images/sections/ebso/lock-bso.jpg'"
      :titleColor="'#FFFFFF'" :textColor="'rgba(255, 255, 255, 0.85)'" />

    <card-grid-section :items="lockStepItems" :columns="5" :paddingY="60" align="center"
      :backgroundColor="'#040428'" :headerBackground="'transparent'" :headerTextColor="'#01dba8'"
      :textColor="'#ffffff'" :cardBorderColor="'#4656FF'" :bodyTextTransform="'uppercase'"
      :cardMinHeight="'89px'" :cardBackground="'linear-gradient(102.01deg, rgba(70, 86, 255, 0.3) 0%, rgba(70, 86, 255, 0) 100%)'"
      :gap="'16px'" :headerMinHeight="'28px'" :bodyPadding="'0 10px 12px'" :mobileColumns="3" />

    <tier-table :headers="tierHeaders" :rows="tierRows" :note="$t('ebsoScreen.lockTiers.note')"
      :columnWidths="['14%', '21%', '18%', '19%', '28%']" />

    <card-grid-section :title="$t('ebsoScreen.supply.title')" :items="supplyPanels" :columns="2"
      :cardBodyBackground="'linear-gradient(84.08deg, #E8E8FF -10.67%, #C2C5FF 108.08%)'"
      :borderTop="'1px solid #6464FF'" :mobileAlign="'left'">
      <template v-slot:footer>
        <div class="supply-actions">
          <base-button :text="$t('ebsoScreen.supply.buttons.etherscan')" size="large" type="light-green"
            @clicked="handleEtherscan" />
          <base-button :text="$t('ebsoScreen.supply.buttons.copy')" size="large" type="light-green"
            @clicked="handleCopyContract" />
          <base-button :text="$t('ebsoScreen.supply.buttons.wallet')" size="large" type="light-green"
            @clicked="handleAddToWallet" />
        </div>
        <div class="supply-notes">
          <p class="supply-note">
            <img src="/images/icons/warning.svg" class="supply-note__icon" alt="" />
            {{ $t('ebsoScreen.supply.notes.first') }}
          </p>
          <p class="supply-note">
            <img src="/images/icons/warning.svg" class="supply-note__icon" alt="" />
            {{ $t('ebsoScreen.supply.notes.second') }}
          </p>
          <p class="supply-note">
            <img src="/images/icons/information.svg" class="supply-note__icon" alt="" />
            {{ $t('ebsoScreen.supply.notes.third') }}
          </p>
        </div>
      </template>
    </card-grid-section>

    <whats-next-section :title="$t('ebsoScreen.whatsNext.title')" :slides="whatsNextSlides"
      :prevLabel="$t('ebsoScreen.whatsNext.prev')" :nextLabel="$t('ebsoScreen.whatsNext.next')"
      :backgroundImage="'/images/screens/wallet/desktop/key-features-bg.jpg'" />

    <split-section :title="$t('ebsoScreen.recap.title')" :body="$t('ebsoScreen.recap.body')"
      :imageAlt="$t('ebsoScreen.recap.imageAlt')" imageSide="left"
      :imageSrc="`/images/sections/ebso/redemption${imageLocaleSuffix}.jpg`"
      :visualAspectRatio="'1818 / 1641'"
      :borderBottom="'1px solid #6464FF'" />

    <card-grid-section :title="$t('ebsoScreen.trust.title')" :items="trustItems" :columns="3" align="center"
      :headerBackground="'transparent'" :headerTextColor="'#050535'" :headerDirection="'column'"
      :labelTextTransform="'none'" />

    <card-grid-section :title="$t('ebsoScreen.howToGetEbso.title')" :items="howToGetItems" :columns="4"
      :note="$t('ebsoScreen.howToGetEbso.note')" :backgroundColor="'#040428'" :titleColor="'#ffffff'"
      :textColor="'#ffffff'" :headerBackground="'transparent'" :headerTextColor="'#ffffff'"
      :cardBorderColor="'#4656FF'" :backgroundImage="'/images/sections/ebso/how-to-get-background.jpg'"
      :iconPosition="'end'" :headerBorderBottom="'1px solid #4656FF'"
      :separatorIcon="'/images/icons/arrow-blue.svg'" :noteIcon="'/images/icons/information.svg'"
      :iconSize="'32px'" :cardBackground="'linear-gradient(102.01deg, rgba(70, 86, 255, 0.3) 0%, rgba(70, 86, 255, 0) 100%)'" />

    <faq-section :title="$t('ebsoScreen.faq.title')" :items="faqItems" :prompt="$t('ebsoScreen.faq.prompt')"
      :buttonText="$t('ebsoScreen.faq.button')" @contactClicked="handleSupport" />

    <split-section :title="$t('ebsoScreen.closing.title')" :body="$t('ebsoScreen.closing.body')" imageSide="right"
      :imageSrc="'/images/elements/home/web/home_phone.png'" imageFit="contain" :panelColor="'transparent'"
      :imagePosition="'right bottom'"
      :backgroundImage="'/images/elements/home/web/wallet_background.png'" :backgroundOpacity="0.35"
      :borderBottom="'1px solid #6464FF'" visualInContainer mobileImageLast
      :mobileImageMaxWidth="'215px'">
      <template v-slot:footer>
        <div class="closing-actions">
          <base-button :text="$t('ebsoScreen.closing.buyButton')" size="large" type="light-green"
            @clicked="handleBuyClick" />
          <!-- the design replaced the single wallet button with the two store buttons -->
          <!-- <base-button :text="$t('ebsoScreen.closing.walletButton')" size="large" type="light-green"
            @clicked="handleOpenWallet" /> -->
          <store-button version="apple" />
          <store-button version="google" />
        </div>
        <a class="closing-link" :href="whitepaperUrl" target="_blank" rel="noopener">
          {{ $t('ebsoScreen.closing.whitepaperLink') }}
        </a>
      </template>
    </split-section>

    <!-- Replaced by the sections above, kept until the rebuild is signed off: -->
    <!-- <video-section /> -->
    <!-- <introduction-section /> -->
    <!-- <gold-coverage-section /> -->
    <!-- <bso-and-ebso-section /> -->
    <!-- <audit-and-steps-section /> -->
    <!-- <token-datas-section /> -->
    <!-- <partners-section /> -->
    <!-- <warning-section :backgroundColor="'#1B1B1B'" /> -->
    <!-- <other-products
      hiddenProductKey="ebso"
      cardBorderColor="#E8A83B"
      cardShadow="none"
      cardBackgroundColor="transparent"
      cardDescriptionColor="#fff"
    /> -->
  </div>
</template>

<script>
import SplitSection from "@/components/sections/SplitSection";
import LinksSection from "@/components/sections/ebso/LinksSection";
import CardGridSection from "@/components/sections/ebso/CardGridSection";
import TierTable from "@/components/sections/ebso/TierTable";
import WhatsNextSection from "@/components/sections/ebso/WhatsNextSection";
import FaqSection from "@/components/sections/ebso/FaqSection";
import VideoSection from "@/components/sections/ebso/VideoSection";
import IntroductionSection from "@/components/sections/ebso/IntroductionSection";
import GoldCoverageSection from "@/components/sections/ebso/GoldCoverageSection";
import AuditAndStepsSection from "@/components/sections/ebso/AuditAndStepsSection";
import TokenDatasSection from "@/components/sections/ebso/TokenDatasSection";
import PartnersSection from "@/components/sections/ebso/PartnersSection";
import OtherProducts from "@/components/sections/OtherProducts";
import BsoAndEbsoSection from "@/components/sections/ebso/BsoAndEbsoSection";
import WarningSection from "@/components/sections/bit/WarningSection";
import HoloverzWhitePaper from "@/components/sections/holoverz/HoloverzWhitePaper";
import StoreButton from "@/components/elements/StoreButton";
import Api from "@/api";

export default {
  name: "EbsoScreen",
  props: {},
  components: {
    SplitSection,
    LinksSection,
    CardGridSection,
    TierTable,
    WhatsNextSection,
    FaqSection,
    VideoSection,
    IntroductionSection,
    GoldCoverageSection,
    AuditAndStepsSection,
    TokenDatasSection,
    PartnersSection,
    OtherProducts,
    BsoAndEbsoSection,
    WarningSection,
    HoloverzWhitePaper,
    StoreButton
  },
  data: () => ({
    lbankUrl: "https://www.lbank.com/trade/ebso_usdt",
    // live supply figures from /token-supply (same-origin in production,
    // proxied to blockben.com by the dev server, see vue.config.js)
    tokenSupply: null,
    whitepaperUrl: "https://data.blockben.com/whitepaper/docs.html",
    // checksummed casing, because it is what the Copy button puts on the clipboard
    contractAddress: "0x866f8a50A64e68ca66E97e032c5Da99538B3F942",
    etherscanUrl:
      "https://etherscan.io/token/0x866f8a50a64e68ca66e97e032c5da99538b3f942",
    // Brand names, so no i18n. Empty link => rendered but not clickable.
    externalLinks: [
      {
        label: "Etherscan",
        icon: "/images/icons/etherscan.svg",
        link: "https://etherscan.io/token/0x866f8a50a64e68ca66e97e032c5da99538b3f942",
      },
      {
        label: "CoinMarketCap",
        icon: "/images/icons/coinmarketcap.svg",
        link: "https://coinmarketcap.com/currencies/eblockstock/",
      },
      {
        // TODO links: no CoinGecko listing yet
        label: "CoinGecko",
        icon: "/images/icons/coingecko.svg",
        link: "",
      },
      {
        label: "CertiK",
        icon: "/images/icons/certik.svg",
        link: "https://skynet.certik.com/projects/eblockstock",
      },
      {
        label: "Telegram",
        icon: "/images/icons/telegram-circle.svg",
        link: "https://t.me/BlockBen_Official",
      },
      {
        label: "",
        icon: "/images/icons/x-mark.svg",
        link: "https://x.com/BlockBen",
      },
    ],
  }),
  created() { },
  mounted() {
    this.fetchTokenSupply();
  },
  computed: {
    // Three of the artworks (bso-vs-ebso, growing-utility, redemption) have
    // their copy baked into the export, so each language has its own file next
    // to the English one (-hu, -lv). Anything else falls back to the English
    // render.
    imageLocaleSuffix() {
      return ["hu", "lv"].includes(this.$i18n.locale)
        ? `-${this.$i18n.locale}`
        : "";
    },
    whatYouBuyItems() {
      const icons = {
        twoForms: "buy-two-forms",
        everyone: "buy-everyone",
        benefits: "buy-benefits",
        recap: "buy-recap",
      };
      return ["twoForms", "everyone", "benefits", "recap"].map((key) => ({
        icon: `/images/icons/${icons[key]}.svg`,
        label: this.$t(`ebsoScreen.whatYouBuy.items.${key}.label`),
        body: this.$t(`ebsoScreen.whatYouBuy.items.${key}.body`),
      }));
    },
    // `label` is the step number, `body` the caption under it
    lockStepItems() {
      return ["hold", "lock", "tier", "buy", "unlock"].map((key, i) => ({
        label: `${i + 1}.`,
        body: this.$t(`ebsoScreen.lockSteps.${key}`),
      }));
    },
    tierColumns() {
      return ["tier", "locked", "multiplier", "fee", "benefits"];
    },
    tierHeaders() {
      return this.tierColumns.map((col) =>
        this.$t(`ebsoScreen.lockTiers.headers.${col}`)
      );
    },
    tierRows() {
      return ["i", "ii", "iii", "iv", "v", "legend"].map((row) =>
        this.tierColumns.map((col) =>
          this.$t(`ebsoScreen.lockTiers.rows.${row}.${col}`)
        )
      );
    },
    supplyPanels() {
      // the figures come from the token-supply endpoint when it answers;
      // the i18n strings stay as the fallback (and for the non-numeric rows)
      // the audit and listing values double as links to their source
      const rowLinks = {
        audit: "https://skynet.certik.com/projects/eblockstock",
        listedOn: this.lbankUrl,
      };
      const panel = (key, rowKeys) => ({
        label: this.$t(`ebsoScreen.supply.${key}.label`),
        rows: rowKeys.map((row) => ({
          label: this.$t(`ebsoScreen.supply.${key}.${row}`),
          value:
            this.liveSupplyValue(key, row) ??
            this.$t(`ebsoScreen.supply.${key}.${row}Value`),
          link: rowLinks[row],
        })),
      });
      return [
        panel("qantrum", [
          "totalSupply",
          "circulating",
          "onEthereum",
          "locked",
          "promotion",
          "collaboration",
          "reserves",
          "blockben",
        ]),
        panel("ethereum", [
          "currentSupply",
          "contract",
          "network",
          "audit",
          "listedOn",
        ]),
      ];
    },
    whatsNextSlides() {
      // only the first quarter is under way; the rest render the pale "Planned" tab
      const slides = [
        { key: "q3y26", entries: ["lbank", "tiers", "docs"], current: true },
        { key: "q4y26", entries: ["projects", "allocation", "recap"] },
        { key: "q1y27", entries: ["listings", "partners", "benefits"] },
        { key: "q2y27", entries: ["one"] },
      ];
      return slides.map((slide) => ({
        period: this.$t(`ebsoScreen.whatsNext.slides.${slide.key}.period`),
        status: this.$t(`ebsoScreen.whatsNext.slides.${slide.key}.status`),
        title: this.$t(`ebsoScreen.whatsNext.slides.${slide.key}.title`),
        statusHighlighted: !!slide.current,
        entries: slide.entries.map((entry) =>
          this.$t(`ebsoScreen.whatsNext.slides.${slide.key}.entries.${entry}`)
        ),
      }));
    },
    trustItems() {
      return ["licence", "security", "entities"].map((key) => ({
        label: this.$t(`ebsoScreen.trust.items.${key}.label`),
        body: this.$t(`ebsoScreen.trust.items.${key}.body`),
        icon: `/images/icons/trust-${key}.svg`,
      }));
    },
    howToGetItems() {
      return ["register", "fund", "buy", "bring"].map((key, i) => ({
        label: `${i + 1}.`,
        icon: `/images/icons/step-${key}.svg`,
        title: this.$t(`ebsoScreen.howToGetEbso.items.${key}.title`),
        body: this.$t(`ebsoScreen.howToGetEbso.items.${key}.body`),
      }));
    },
    faqItems() {
      return ["one", "two", "three", "four", "five", "six"].map((key) => ({
        question: this.$t(`ebsoScreen.faq.items.${key}.question`),
        answer: this.$t(`ebsoScreen.faq.items.${key}.answer`),
      }));
    },
  },
  methods: {
    async fetchTokenSupply() {
      try {
        const response = await Api.getRoot("token-supply");
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.tokenSupply = await response.json();
      } catch (e) {
        // the static i18n figures stay on screen
        console.warn("token-supply request failed, showing static figures", e);
      }
    },
    liveSupplyValue(panelKey, row) {
      // response shape: { totalSupply, circulatingSupply, onEthereum,
      //   lockedInStockLock, promotionAllocation, collaborationAllocation,
      //   generalReserves, blockBenSia } -- numbers, or null when not yet known
      const fields = {
        qantrum: {
          totalSupply: "totalSupply",
          circulating: "circulatingSupply",
          onEthereum: "onEthereum",
          locked: "lockedInStockLock",
          promotion: "promotionAllocation",
          collaboration: "collaborationAllocation",
          reserves: "generalReserves",
          blockben: "blockBenSia",
        },
        ethereum: {
          // every eBSO on Ethereum is a BSO held on the platform, same figure
          currentSupply: "onEthereum",
        },
      };
      const field = (fields[panelKey] || {})[row];
      if (!field || !this.tokenSupply) return null;
      const raw = this.tokenSupply[field];
      // null (e.g. StockLock before launch) keeps the i18n placeholder ("Soon")
      if (raw === undefined || raw === null || raw === "") return null;
      return this.formatSupply(raw);
    },
    formatSupply(raw) {
      const number = Number(raw);
      if (!Number.isFinite(number)) return String(raw);
      // matches the static figures: space as the thousands separator
      return Math.round(number)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    handleBuyClick() {
      window.open(this.lbankUrl, "_blank");
    },
    handleEtherscan() {
      window.open(this.etherscanUrl, "_blank");
    },
    handleCopyContract() {
      // navigator.clipboard needs a secure context; fall back to a temp input
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.contractAddress);
        return;
      }
      const helper = document.createElement("input");
      helper.value = this.contractAddress;
      document.body.appendChild(helper);
      helper.select();
      document.execCommand("copy");
      document.body.removeChild(helper);
    },
    handleAddToWallet() {
      // TODO links: no destination yet. The Etherscan token page stood in for it
      // until a real one arrives, but that is not what the button says, so it
      // does nothing for now:
      // window.open(this.etherscanUrl, "_blank");
    },
    handleOpenWallet() {
      // TODO links: no wallet destination yet
      this.$router.push("/wallet");
    },
    handleSupport() {
      window.open("mailto:support@blockben.com", "_blank");
    },
  },
};
</script>

<style lang='scss' scoped>
#ebso-screen {
  // old gold theme:
  // background-color: $dark-grey;
  // color: $white;
  background-color: #e8e8ff;
  color: $text-color;
}

// The hero reads lighter than the mockup. Figma's Color blend cannot be the
// reason: it replaces hue and saturation but keeps the backdrop's luminance, so
// it cannot darken -- measured, the exported photo and the same photo under a
// #4656FF Color layer both average luminance 110. The export is simply lighter
// than what the Figma frame shows, so this is a plain dark wash instead; tune
// the alpha to taste. Needs the id to outweigh Jumbotron's own
// `.jumbotron.has-bgimage { background: ... }` shorthand.
#ebso-screen .jumbotron.has-bgimage {
  background-image: linear-gradient(
      rgba(5, 5, 53, 0.2),
      rgba(5, 5, 53, 0.2)
    ),
    url("/images/sections/ebso/hero-background.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.jumbotron {
  // border-bottom: 1px solid $ebso-section-border-color;

  &::v-deep {
    .content-wrapper {
      @media (max-width: $breakpoint-xs) {
        padding-top: 130px;
      }
    }

    // the hero content is vertically centred by default, which dropped the logo
    // ~110px down while the badge sat at 54px; pin both to the top instead
    .jumbotron-row {
      align-items: flex-start;
    }

    // .top-section carries Bootstrap's pt-0 (padding-top: 0 !important) when no
    // storesLabel is passed, so the offset goes on the wrapper instead
    .content-wrapper {
      padding-top: 54px;
    }

    .top-section {
      .logo {
        max-height: 75px;
        margin-bottom: 34px;
      }

      // i18n keeps natural casing so translators aren't shouting; the mockup
      // renders the headline in caps
      .title {
        text-transform: uppercase;
      }
    }

    // Between the tablet and desktop breakpoints the headline wraps to six
    // lines in a half-width column and the button drops out through the bottom
    // of the fixed 705px hero. Widening the copy column to 8/12 (the second
    // column is empty on this hero anyway) keeps it to four lines and inside
    // the container, without touching the height. Scoped to this screen and to
    // this band, so the other jumbotrons keep their 6/6 split.
    @media (min-width: 768px) and (max-width: $breakpoint-md) {
      .jumbotron-row > .col-md-6 {
        flex: 0 0 66.666667%;
        max-width: 66.666667%;
      }

      .jumbotron-row > .col-md-6.h-100 {
        flex: 0 0 33.333333%;
        max-width: 33.333333%;
      }
    }

    // The badge is pinned 20px from the top below $breakpoint-sm while the logo
    // starts at the wrapper's 54px padding; 60px lines their centres up (the
    // logo is a constant 160x68 below $breakpoint-md, the badge 56px tall).
    @media (max-width: $breakpoint-sm) {
      .badge-wrapper {
        top: 60px;
      }
    }

    // the mockup centres the hero copy and stretches the CTA once stacked
    @media (max-width: $breakpoint-sm) {
      .top-section {
        text-align: center;

        .logo {
          margin-bottom: 24px;
        }

        // the class sits on the button element itself, not on a wrapper
        .jumbotron-button {
          width: 100%;
          margin-top: 40px;
        }
      }
    }
  }
}

// the shared whitepaper strip keeps its 255px button elsewhere; here it runs
// the full width of the phone, like every other call to action on this page
.holoverz-whitepaper-section {
  @media (max-width: $breakpoint-sm) {
    &::v-deep .whitepaper-button {
      width: 100%;
    }
  }
}

// slot content is compiled in this component's scope, so no ::v-deep needed
.mica-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: $light-green;

  &__icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }

  &__label {
    color: $black;
    font-family: "HKNova-Bold", sans-serif;
    font-size: $text-m;
    line-height: 18px;
    text-transform: uppercase;
    max-width: 90px;
  }

  @media (max-width: $breakpoint-sm) {
    padding: 10px 16px;

    &__icon {
      width: 24px;
      height: 24px;
    }
  }
}

.supply-actions {
  display: flex;
  justify-content: center;
  gap: 30px;

  &::v-deep .default {
    // the design gives all three the same width
    width: 250px;
  }

  // 250px each no longer fits three across on a tablet, and the labels wrap
  // inside the fixed height; let them size to their text instead
  @media (max-width: $breakpoint-md) {
    gap: 16px;

    &::v-deep .default {
      width: auto;
    }
  }

  @media (max-width: $breakpoint-sm) {
    flex-direction: column;
    gap: 14px;

    &::v-deep .default {
      width: 100%;
    }
  }
}

.supply-notes {
  display: grid;
  // one per button above
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 50px;

  @media (max-width: $breakpoint-sm) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.supply-note {
  max-width: 350px;
  margin: 0 auto;
  text-align: center;
  font-size: $text-m;
  line-height: 20px;
  color: $text-color;

  &__icon {
    width: 24px;
    height: 24px;
    display: block;
    margin: 0 auto 10px;
  }
}

.closing-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  // StoreButton is a fixed 45px tall everywhere else on the site; here it sits
  // next to a large Button, so it takes that button's 60px instead. The tighter
  // side padding is what keeps all three on one row with the longest labels
  // (Hungarian) at the 1200px content width.
  &::v-deep .store-button {
    height: 60px;
    padding: 0 10px;
    // the shared button is #010101 everywhere else; this section's design puts
    // it on the page's navy instead
    background-color: #050535;

    .button-text {
      max-height: 34px;
    }
  }

  @media (max-width: $breakpoint-sm) {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;

    &::v-deep .default {
      width: 100%;
    }

    // the store buttons stack full width like the CTA above them, with their
    // artwork centred instead of pinned left
    &::v-deep .store-button {
      justify-content: center;
      padding: 0;
    }
  }
}

.closing-link {
  display: inline-block;
  margin-top: 26px;
  font-size: $text-m;
  color: #4656ff !important;
  // global.scss puts every <a> in HKNova-Bold/700; the design has this one at
  // the regular weight, and the heavier stroke was what made it read as a
  // darker blue than the #4656FF it has always been
  font-family: "HKNova", sans-serif;
  font-weight: 400;
  text-decoration: underline;

  &:hover {
    color: #4656ff;
  }
}

.supply-pill {
  display: inline-block;
  padding: 10px 18px;
  border: 1px solid #6464ff;
  background: transparent;
  color: #4656ff;
  font-family: "HKNova-Bold", sans-serif;
  font-size: $text-m;
  line-height: 20px;
}

.other-products {
  &::v-deep {
    .section-title {
      color: $blockstock-yellow;
    }
  }
}

.warning-section {
  border-bottom: 1px solid $ebso-section-border-color;
}

// background / button colour / side are driven by props on the component now:
// .holoverz-whitepaper-section {
//   &::v-deep {
//     background: #1B1B1B;
//     border-bottom: 1px solid $ebso-section-border-color;
//   }
// }

</style>
